import { createEventHook } from "@vueuse/core";
import { computed, ref, readonly, watchEffect } from "vue";

/**
 *
 * @param {FileList | DataTransfer | import('vue').Ref<FileList | DataTransfer>} files
 * @returns { FileList | null }
 */
function prepareInitialFiles(files) {
  const _files = files?.value;

  if (!_files) return null;

  if (_files instanceof FileList) return _files;

  const dt = new DataTransfer();

  for (const file of _files) dt.items.add(file);

  return dt.files;
}

/**
 * @typedef {{
 *    multiple?: import("vue").Ref<boolean>,
 *    disabled?: import("vue").Ref<boolean>,
 *    accept?: import("vue").Ref<string>,
 *    reset?: import("vue").Ref<boolean>,
 *    input?: import('vue').ComputedRef<HTMLInputElement>
 *    initialFiles?: import('vue').Ref<FileList>
 * }} TFileDialogOptions
 */

/** @type {TFileDialogOptions} */
const DEFAULT_OPTIONS = {
  multiple: true,
  accept: "*",
  reset: false,
  directory: false,
};

/**
 * @param {TFileDialogOptions} [options={}]
 */
export function useFileDialog(options = {}) {
  const files = ref(prepareInitialFiles(options.initialFiles));
  const change = createEventHook();
  const cancel = createEventHook();
  const { trigger: triggerReset, on: onReset } = createEventHook();

  const inputRef = computed(() => {
    const input = document.createElement("input");

    if (input) {
      input.type = "file";
      input.onchange = (event) => {
        /** @type {HTMLInputElement} */
        const result = event.target;
        files.value = result.files;

        change.trigger(files.value);
      };

      input.oncancel = (_event) => {
        cancel.trigger();
      };
    }

    return input;
  });

  /**
   * @param {TFileDialogOptions} localOption
   */
  function applyOptions(options = {}) {
    const $el = inputRef.value;

    if (!$el) return;

    $el.multiple = options.multiple?.value;
    $el.accept = options.accept?.value;
  }

  function reset() {
    files.value = [];
    triggerReset(null);
    if (inputRef.value && inputRef.value.value) {
      inputRef.value.value = "";
      change.trigger(null);
    }
  }

  /**
   * @param {TFileDialogOptions} localOption
   */
  function open(localOption = {}) {
    if (!!options.disabled?.value) return;

    const $el = inputRef.value;

    if (!$el) return;

    const mergedOptions = {
      ...DEFAULT_OPTIONS,
      ...options,
      ...localOption,
    };

    applyOptions(mergedOptions);
    if (mergedOptions.reset) reset();

    $el.click();
  }

  watchEffect(() => applyOptions(options));

  return {
    file: readonly(files),
    open,
    reset,
    onCancel: cancel.on,
    onChange: change.on,
    onReset,
  };
}
